import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Tooltip,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import api from "../../../../Apis";
import TrainingHistorySidebar from "./Components/TrainingHistorySidebar";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AssistantChat = () => {
  const { fanpage_id, ai_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [cookies] = useCookies("is_login");
  const { t } = useTranslation();
  const elementRef = useRef(null);
  const inputFile = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const [state, setState] = React.useState({
    allMessages: [],
    conversation_id: null,
    attachments: [],
    dialog: false,
    value: "",
    loading: false,
    isAITyping: false,
    isSending: false,
  });

  const handleClickOpenSelectImage = () => {
    inputFile.current.click();
  };

  const onChangeImage = (e) => {
    const files = e.target.files;
    console.log(files, "files");
  };

  // Fetch messages ban đầu
  const fetchMessages = async (showLoading = false) => {
    if (showLoading) {
      setState((prevState) => ({ ...prevState, loading: true }));
    }

    try {
      const res = await fetch(`${api.gpt_assistant_get_ai_chat}?ai_bot_id=${ai_id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookies.is_login}`,
        },
      });
      const data = await res.json();

      if (data?.success) {
        const mappedMessages = (data?.datas || []).map((msg) => ({
          id: msg.id,
          message: msg.content,
          attachment: [],
          is_hidden: 0,
          sender_name: msg.sender === "AI" ? "Trợ lý AI" : "Bạn",
          sender_id: msg.sender === "AI" ? "AI" : "Owner",
          created_at: new Date(msg.created_at).toLocaleString("vi-VN"),
          reply_to: {
            text: null,
            attachment: null,
          },
          position_mess: msg.sender === "AI" ? "mess-left" : "mess-right",
        }));

        setState((prevState) => ({
          ...prevState,
          allMessages: mappedMessages,
          conversation_id: data?.conversation_id || null,
          loading: false,
        }));
      } else {
        console.error("API Error:", data?.message);
        setState((prevState) => ({ ...prevState, loading: false }));
      }
    } catch (error) {
      console.log(error);
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  const handleSendMessage = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (state.isSending || !state.value.trim()) return;

    const messageToSend = state.value;
    const tempUserMessageId = `user_${Date.now()}`;

    // Tạo tin nhắn user
    const newUserMessage = {
      id: tempUserMessageId,
      message: messageToSend,
      attachment: [],
      is_hidden: 0,
      sender_name: "Bạn",
      sender_id: "Owner",
      created_at: new Date().toLocaleString("vi-VN"),
      reply_to: {
        text: null,
        attachment: null,
      },
      position_mess: "mess-right",
      sending: true,
    };

    // Thêm tin nhắn user, clear input và hiện typing ngay lập tức
    setState((prevState) => ({
      ...prevState,
      allMessages: [...prevState.allMessages, newUserMessage],
      value: "",
      isSending: true,
      isAITyping: true, // Hiện typing ngay
    }));

    if (textareaRef.current) {
      textareaRef.current.value = "";
    }

    try {
      const response = await fetch(api.gpt_assistant_get_ai_chat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookies.is_login}`,
        },
        body: JSON.stringify({
          content: messageToSend,
          ai_bot_id: ai_id,
        }),
      });

      const data = await response.json();

      if (data?.success) {
        // Tắt typing và reset isSending
        setState((prevState) => ({
          ...prevState,
          isAITyping: false,
          isSending: false,
        }));

        // Gọi lại API để lấy messages mới nhất
        await fetchMessages(false);
      } else {
        // API thất bại - Xóa tin nhắn user và tắt typing
        setState((prevState) => ({
          ...prevState,
          allMessages: prevState.allMessages.filter(msg => msg.id !== tempUserMessageId),
          isAITyping: false,
          isSending: false,
        }));

        Swal.fire({
          icon: "error",
          title: "Lỗi",
          text: data?.message || "Đã xảy ra lỗi khi gửi tin nhắn. Vui lòng thử lại!",
        });
      }
    } catch (error) {
      console.error("Send message error:", error);

      // Lỗi network - Xóa tin nhắn user và tắt typing
      setState((prevState) => ({
        ...prevState,
        allMessages: prevState.allMessages.filter(msg => msg.id !== tempUserMessageId),
        isAITyping: false,
        isSending: false,
      }));

      Swal.fire({
        icon: "error",
        title: "Lỗi",
        text: "Đã xảy ra lỗi khi gửi tin nhắn. Vui lòng kiểm tra kết nối và thử lại!",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();

      if (!state.isSending && state.value.trim()) {
        handleSendMessage();
      }
    }
  };

  // Load messages ban đầu
  useEffect(() => {
    fetchMessages(true);
  }, [cookies.is_login]);

  // Auto scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [state.allMessages, state.isAITyping]);

  const TypingIndicator = () => (
    <div className="m-b-20">
      <div className="mess-left clear">
        <div className="avatar">
          <Avatar />
        </div>
        <div className="mess-text" style={{
          padding: '12px 16px',
          backgroundColor: '#f0f0f0',
          borderRadius: '18px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
          <span className="typing-dot"></span>
        </div>
      </div>
      <style>{`
        .typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #90949c;
          animation: typingAnimation 1.4s infinite ease-in-out;
          display: inline-block;
        }
        .typing-dot:nth-child(1) {
          animation-delay: 0s;
        }
        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes typingAnimation {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.7;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );

  return (
    <Container maxWidth="xl" className="">
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box>
            <div className="messenger-box"
              style={{
                position: "static",
                display: "block"
              }}
            >
              <div className="chat" style={{ width: "100%"  }}>
                <div className="h-100"  style={{
                  height: "calc(100vh - 70px)"
                }}>
                  <div className="info-user-chat" style={{ height: "6%" }}>
                    <div className="info">
                      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                          <ArrowBackIcon sx={ {fontSize: 16, cursor: "pointer"}} onClick={() => navigate(`/dashboard/ai`)} />
                        <div className="full-name">Chat với Bot <b>{location.state?.ai_name}</b></div>  
                      </Box>
                    </div>
                  </div>
                  <div className="detail-mess" style={{ height: "73%" }}>
                    <div>
                      {state.loading ? (
                        <center>
                          <CircularProgress />
                        </center>
                      ) : (
                        <div>
                          {Array.isArray(state.allMessages) ? (
                            state.allMessages.map((value) => {
                              return (
                                <div className="m-b-20" key={value.id}>
                                  {value.message != null && value.message !== "" ? (
                                    value.position_mess === "mess-left" ? (
                                      <div className={value.position_mess + " clear"}>
                                        <div className="avatar">
                                          <Avatar />
                                        </div>
                                        <Tooltip
                                          arrow
                                          title={value.created_at}
                                          placement="right"
                                        >
                                          <div className="mess-text" style={{ whiteSpace: 'pre-wrap' }}>
                                            {value.message}
                                          </div>
                                        </Tooltip>
                                      </div>
                                    ) : (
                                      <div className={value.position_mess + " clear"}>
                                        <Tooltip
                                          arrow
                                          title={value.created_at}
                                          placement="left"
                                        >
                                          <div className="mess-text" style={{ whiteSpace: 'pre-wrap' }}>
                                            {value.message}
                                            {value.sending && (
                                              <CheckCircleRoundedIcon
                                                className="check-send"
                                                style={{ marginLeft: '8px', fontSize: '14px', verticalAlign: 'middle' }}
                                              />
                                            )}
                                          </div>
                                        </Tooltip>
                                      </div>
                                    )
                                  ) : (
                                    ""
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <center>{state.allMessages}</center>
                          )}

                          {state.isAITyping && <TypingIndicator />}
                        </div>
                      )}
                    </div>
                    <div ref={messagesEndRef}></div>
                    <div ref={elementRef}></div>
                  </div>
                  <div className="write-mess" style={{height:  "16%"}}>
                    <div
                      className="form-chat"
                      style={{
                        marginTop: 0,
                        paddingTop: 10,
                      }}
                    >
                      <Avatar />
                      <form onSubmit={handleSendMessage}>
                        <Dialog
                          fullWidth={true}
                          maxWidth="sm"
                          open={state.dialog}
                          onClose={() => setState({ ...state, dialog: false })}
                        >
                          <DialogTitle
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            {t("liveChat.conversation.attachedImage")}
                            <Button
                              startIcon={<CloseOutlinedIcon />}
                              variant="contained"
                              color="warning"
                              onClick={() =>
                                setState({ ...state, dialog: false })
                              }
                            >
                              {t("common.close")}
                            </Button>
                          </DialogTitle>
                          <DialogContent>
                            <Box sx={{ flexGrow: 1 }}>
                              <Grid container spacing={2}>
                                {state.attachments.map((attachmentItem, key) => {
                                  return (
                                    <Grid
                                      item
                                      xs={2}
                                      key={key}
                                      sx={{ position: "relative" }}
                                    >
                                      <div className="image-attachment position-relative">
                                        <img
                                          className="w-100"
                                          src={attachmentItem.image_data}
                                          alt=""
                                        />
                                        <CloseOutlinedIcon className="cursor" />
                                      </div>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Box>
                          </DialogContent>
                        </Dialog>
                        {state.attachments.length > 0 && (
                          <div
                            style={{
                              padding: "0px 10px",
                              marginBottom: "10px",
                            }}
                            className="attachments"
                          >
                            <Chip
                              label={
                                state.attachments.length +
                                " " +
                                t("liveChat.conversation.attachedImage")
                              }
                              icon={<ImageOutlinedIcon />}
                              color="warning"
                            />
                          </div>
                        )}
                        <textarea
                          ref={textareaRef}
                          placeholder={t("liveChat.conversation.pressEnterToSend")}
                          value={state.value}
                          onChange={(e) =>
                            setState({ ...state, value: e.target.value })
                          }
                          onKeyDown={handleKeyDown}
                          style={{
                            lineHeight: '1.6',
                            paddingTop: '8px',
                            paddingBottom: '8px'
                          }}
                        />
                        {state.attachments.length > 0 && (
                          <style
                            dangerouslySetInnerHTML={{
                              __html:
                                ".detail-mess{height: 50% !important} .write-mess{height: 42% !important} .messenger-box .chat .write-mess .tag {height: 20%} .messenger-box .chat .write-mess .form-chat form textarea {height: 70%} .messenger-box .chat .write-mess .form-chat {height: calc(65% - 30px)}",
                            }}
                          />
                        )}
                      </form>
                    </div>
                    <div style={{ height: "20%" }}>
                      <div className="attachment">
                        <input
                          style={{ display: "none" }}
                          ref={inputFile}
                          onChange={onChangeImage}
                          multiple
                          type="file"
                          className="file"
                          accept="image/*"
                        />
                        <Tooltip
                          arrow
                          onClick={handleClickOpenSelectImage}
                          title={t("liveChat.conversation.sendImage")}
                          placement="top"
                        >
                          <ImageOutlinedIcon />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box>
            <TrainingHistorySidebar is_login={cookies.is_login} />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AssistantChat;